import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import InvoiceTable from '../../../components/tables/InvoiceTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useInvoiceApi } from '../../../hooks/useInvoiceApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { InvoiceDTO, ClientDTO, InvoiceStatusDTO, InvoiceTypeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useClientApi } from '../../../hooks/useClientApi';
import { useInvoiceStatusApi } from '../../../hooks/useInvoiceStatusApi';
import { useInvoiceTypeApi } from '../../../hooks/useInvoiceTypeApi';

const InvoiceList: React.FC = () => {
  const { search, remove, isLoading } = useInvoiceApi();
  const { search: searchClients, isLoading: isLoadingClients } = useClientApi();
  const { search: searchInvoiceStatuses, isLoading: isLoadingInvoiceStatuses } = useInvoiceStatusApi();
  const { search: searchInvoiceTypes, isLoading: isLoadingInvoiceTypes } = useInvoiceTypeApi();
  const [items, setItems] = useState<InvoiceDTO[]>([]);
  const [clients, setClients] = useState<ClientDTO[]>([]);
  const [invoiceStatuses, setInvoiceStatuses] = useState<InvoiceStatusDTO[]>([]);
  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceTypeDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Invoices', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Invoices:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await searchClients({
        page: 1,
        pageSize: 100,
        sortBy: 'Name',
        sortDescending: false,
      });
      setClients(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const loadInvoiceStatuses = async () => {
    try {
      const response = await searchInvoiceStatuses({
        page: 1,
        pageSize: 100,
        sortBy: 'Name',
        sortDescending: false,
      });
      setInvoiceStatuses(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching invoice statuses:', error);
    }
  };

  const loadInvoiceTypes = async () => {
    try {
      const response = await searchInvoiceTypes({
        page: 1,
        pageSize: 100,
        sortBy: 'Name',
        sortDescending: false,
      });
      setInvoiceTypes(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching invoice types:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
    loadClients();
    loadInvoiceStatuses();
    loadInvoiceTypes();
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: InvoiceDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting Invoice:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/invoices"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Manage.Invoice.View"
    >
      <Card
        title="Manage Invoices"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/invoices/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <FormField
              id="clientId"
              label="Search by Client"
              type="dropdown"
              value={searchFilters.clientId || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, clientId: e.target.value })
              }
              placeholder="All Clients"
              options={clients.map((client) => ({
                value: client.id,
                label: client.name,
              }))}
              isLoading={isLoadingClients}
            />
            <FormField
              id="invoiceNumber"
              label="Search by Invoice Number"
              value={searchFilters.invoiceNumber || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  invoiceNumber: e.target.value,
                })
              }
              placeholder="Search by Invoice Number"
            />
            <FormField
              id="invoiceStatusId"
              label="Invoice Status"
              type="dropdown"
              value={searchFilters.invoiceStatusId || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  invoiceStatusId: e.target.value,
                })
              }
              placeholder="All Statuses"
              options={invoiceStatuses.map((status) => ({
                value: status.id,
                label: status.name,
              }))}
              isLoading={isLoadingInvoiceStatuses}
            />
            <FormField
              id="invoiceTypeId"
              label="Invoice Type"
              type="dropdown"
              value={searchFilters.invoiceTypeId || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  invoiceTypeId: e.target.value,
                })
              }
              placeholder="All Types"
              options={invoiceTypes.map((type) => ({
                value: type.id,
                label: type.name,
              }))}
              isLoading={isLoadingInvoiceTypes}
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <InvoiceTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/invoices/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default InvoiceList;
